import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Megaphone } from 'lucide-react';
import type { EventKind } from '@ready/shared';
import { courseUrl, pathUrl, quizUrl } from '@/lib/api';
import { LINKEDIN_TAG_TIP, linkedInPostText } from '@/lib/linkedin';
import { LinkedInShareButton, NativeShareButton } from '@/components/share-buttons';
import { Button } from '@/components/ui';

const PATHS = { quiz: 'q', course: 'c', event: 'e', path: 'paths' } as const;

/**
 * Invites learners to spread a quiz, course, event or learning path: share it
 * on LinkedIn, copy the link, or get campaign posters. Org-only ones skip the
 * public sharing and offer posters for notice boards inside the org. The LinkedIn post comes
 * written (lib/linkedin.ts), tagging Nirvik AI Labs.
 */
export function ShareQuizCard({
  slug,
  title,
  audience,
  heading,
  text,
  kind = 'quiz',
  eventKind = 'workshop',
  moment,
}: {
  slug: string;
  /** For the LinkedIn post. */
  title: string;
  /** Only public items get LinkedIn and link sharing; the rest get posters for members. */
  audience: string;
  heading: string;
  text: string;
  kind?: 'quiz' | 'course' | 'event' | 'path';
  eventKind?: EventKind;
  /** Quizzes: the post says they passed or took it (from the result page). */
  moment?: 'passed' | 'took';
}) {
  const [copied, setCopied] = useState(false);
  const isPublic = audience === 'public';
  const url =
    kind === 'quiz'
      ? quizUrl(slug)
      : kind === 'course'
        ? courseUrl(slug)
        : kind === 'path'
          ? pathUrl(slug)
          : `${window.location.origin}/e/${slug}`;
  const post = linkedInPostText(
    kind === 'quiz'
      ? { kind, title, url, moment }
      : kind === 'course' || kind === 'path'
        ? { kind, title, url }
        : { kind: eventKind, title, url },
  );
  return (
    <section className="@container rounded-[var(--radius)] border-2 border-primary/60 bg-primary/10 p-5">
      <div className="flex items-start gap-3">
        <Megaphone className="mt-0.5 size-6 shrink-0 text-primary" aria-hidden />
        <div>
          <h2 className="text-lg font-semibold">{heading}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{text}</p>
        </div>
      </div>
      <div className={`mt-4 grid gap-3 ${isPublic ? '@xl:grid-cols-3' : ''}`}>
        {isPublic && <LinkedInShareButton post={post} url={url} />}
        {isPublic && <NativeShareButton title={title} text={post} />}
        {isPublic && (
          <Button
            variant="secondary"
            onClick={async () => {
              await navigator.clipboard.writeText(url);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? (
              <Check className="size-4" aria-hidden />
            ) : (
              <Copy className="size-4" aria-hidden />
            )}
            {copied ? 'Link copied' : 'Copy link'}
          </Button>
        )}
        <Link
          to={`/${PATHS[kind]}/${slug}/poster`}
          className="inline-flex items-center justify-center gap-2 rounded-[var(--radius)] border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Get campaign posters
        </Link>
      </div>
      {isPublic && <p className="mt-3 text-xs text-muted-foreground">{LINKEDIN_TAG_TIP}</p>}
    </section>
  );
}
